// utils/imageCanvas.ts — canvas helpers shared by the crop and downscale code.

export type EncodeType = 'image/webp' | 'image/jpeg' | 'image/png';

/** Promise wrapper around canvas.toBlob (null when the type is unsupported). */
export function toBlobAsync(
  canvas: HTMLCanvasElement,
  type: EncodeType,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => { resolve(blob); }, type, quality);
  });
}

/**
 * Load an image (URL or Blob) for drawing. Prefers ImageBitmap, which honors
 * EXIF rotation (iOS photos, etc.); falls back to HTMLImageElement.
 */
export async function loadDrawable(source: string | Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    const blob = typeof source === 'string' ? await (await fetch(source)).blob() : source;
    return await createImageBitmap(blob, { imageOrientation: 'from-image' });
  }
  if (typeof source === 'string') return await createImage(source);
  const url = URL.createObjectURL(source);
  try {
    return await createImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => { resolve(image); });
    image.addEventListener('error', (error) => {
      reject(error instanceof Error ? error : new Error('Failed to load image'));
    });
    image.crossOrigin = 'anonymous';
    image.src = url;
  });
}
